import { CornerDownLeft } from "lucide-react";
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";

import { useEditorMode } from "../../hooks";

interface MentionListProps {
  items: string[];
  command: (params: { id: string }) => void;
  query?: string;
}

export interface MentionListHandle {
  onKeyDown: (params: { event: KeyboardEvent }) => boolean;
}
const CreateHint = forwardRef<
  HTMLButtonElement,
  {
    className?: string;
    variableText: string;
    variant?: "selected";
    onClick?: () => void;
    onMouseEnter?: () => void;
  }
>(({ className, variableText, variant, onClick, onMouseEnter }, ref) => (
  <button
    ref={ref}
    type="button"
    className={`flex w-full items-center justify-between rounded-md px-2 py-1.5 text-gray-500 ${
      variant === "selected" ? "bg-gray-100 text-gray-900" : ""
    } hover:bg-gray-100 hover:text-gray-900 focus:bg-gray-100 focus:text-gray-900 focus:outline-none ${
      className || ""
    }`}
    onClick={onClick}
    onMouseEnter={onMouseEnter}
    tabIndex={-1}
  >
    <span>
      Create{" "}
      <strong className={variant === "selected" ? "text-gray-900" : "text-black"}>
        {variableText}
      </strong>
    </span>
    <kbd
      className={`ml-2 inline-flex aspect-square h-5 w-5 items-center justify-center rounded-md border px-1 py-0.5 text-xs font-semibold ${
        variant === "selected"
          ? "border-gray-400 bg-gray-200 text-gray-700"
          : "border-gray-300 bg-gray-100 text-gray-500"
      }`}
    >
      <CornerDownLeft />
    </kbd>
  </button>
));

const MentionList = forwardRef<MentionListHandle, MentionListProps>((props, ref) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const currentQuery = props.query || "";
  const containerRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const createHintRef = useRef<HTMLButtonElement>(null);

  // Total items includes the CreateHint
  const totalItems = props.items.length + 1;

  const scrollToItem = (index: number) => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    let element: HTMLElement | null = null;
    if (index < props.items.length) {
      element = itemRefs.current[index];
    } else {
      element = createHintRef.current;
    }

    if (element) {
      const containerRect = container.getBoundingClientRect();
      const elementRect = element.getBoundingClientRect();

      if (elementRect.top < containerRect.top) {
        // Element is above the visible area
        element.scrollIntoView({ block: "start", behavior: "smooth" });
      } else if (elementRect.bottom > containerRect.bottom) {
        // Element is below the visible area
        element.scrollIntoView({ block: "end", behavior: "smooth" });
      }
    }
  };

  const selectItem = (index: number) => {
    if (index < props.items.length) {
      // Existing item selected
      props.command({ id: props.items[index] });
    } else {
      // CreateHint selected
      const variableName = currentQuery || "variableName";
      props.command({ id: variableName });
    }
  };

  const upHandler = () => {
    const newIndex = (selectedIndex + totalItems - 1) % totalItems;
    setSelectedIndex(newIndex);
    scrollToItem(newIndex);
  };

  const downHandler = () => {
    const newIndex = (selectedIndex + 1) % totalItems;
    setSelectedIndex(newIndex);
    scrollToItem(newIndex);
  };

  const enterHandler = () => {
    selectItem(selectedIndex);
  };
  useEffect(() => {
    setSelectedIndex(0);
    scrollToItem(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.items]);

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }: { event: KeyboardEvent }) => {
      if (event.key === "ArrowUp") {
        upHandler();
        return true;
      }

      if (event.key === "ArrowDown") {
        downHandler();
        return true;
      }

      if (event.key === "Enter" || event.key === "Tab") {
        event.preventDefault();
        event.stopPropagation();
        enterHandler();
        return true;
      }

      if (event.key === "Escape") {
        // Let the parent handle the escape key to properly close the menu
        return false;
      }

      return false;
    },
  }));

  const mode = useEditorMode();
  const variableText =
    mode === "jinja2"
      ? `{{${currentQuery || "variableName"}}}`
      : `{${currentQuery || "variableName"}}`;

  return (
    <div
      ref={containerRef}
      className="max-h-[200px] min-w-[200px] overflow-y-auto rounded-md border border-gray-200 bg-white p-1 shadow-md"
    >
      {props.items.length ? (
        <>
          {props.items.map((item: string, index: number) => (
            <button
              ref={el => (itemRefs.current[index] = el)}
              className={`w-full rounded-md px-2 py-1.5 text-left text-sm hover:bg-gray-100 focus:bg-gray-100 focus:outline-none ${
                index === selectedIndex ? "bg-gray-100 text-gray-900" : "text-gray-900"
              }`}
              key={item}
              onClick={() => selectItem(index)}
              onMouseEnter={() => setSelectedIndex(index)}
              tabIndex={-1}
            >
              <span className="font-medium">{item}</span>
            </button>
          ))}
          <div className="mt-1 border-t border-gray-100 pt-1">
            <CreateHint
              ref={createHintRef}
              className="text-xs"
              variableText={variableText}
              variant={selectedIndex === props.items.length ? "selected" : undefined}
              onClick={() => selectItem(props.items.length)}
              onMouseEnter={() => setSelectedIndex(props.items.length)}
            />
          </div>
        </>
      ) : (
        <CreateHint
          ref={createHintRef}
          className="text-sm"
          variableText={variableText}
          variant="selected"
          onClick={() => selectItem(0)}
          onMouseEnter={() => setSelectedIndex(0)}
        />
      )}
    </div>
  );
});

MentionList.displayName = "MentionList";
export default MentionList;
